import React, { useReducer } from 'react';
import clsx from 'clsx';
import { ChevronIcon } from '../../assets/icons';
import { getDaysInMonth, isSameDay, isToday, roundTime } from '../../helpers/date';
import Button from '../Button';
import reducer from './DatePicker.reducer';
import { getDayDetails } from './DatePicker.helpers';
import useModalState from '../../context/modal/useModalState';
import moment from 'moment';

type Props = {
    value?: Date;
    onChange: (value?: Date) => void;
};

const ROWS = 6;

const DatePicker: React.FC<Props> = ({ value, onChange }) => {
    const { closeModal } = useModalState();

    const [{ year, month, selected, time }, dispatch] = useReducer(reducer, {
        year: new Date().getFullYear(),
        month: new Date().getMonth(),
        selected: value,
        time: roundTime(value || new Date()),
    });

    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = new Date(year, month).getDay();

    const onClose = () => closeModal('datePickerModal');

    const handleSave = () => {
        if (!selected) return;
        // Include the time in the selected date
        const newDate = new Date(selected);
        newDate.setHours(time.getHours());
        newDate.setMinutes(time.getMinutes());

        onChange(newDate);
        onClose();
    };

    const handleReset = () => {
        onChange(undefined);
        dispatch({ type: 'RESET' });
    };

    return (
        <>
            <div className='current-year'>{year}</div>
            <div className='days-of-the-month'>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <span className='day-label' key={day}>
                        {day}
                    </span>
                ))}
                {[...Array(7 * ROWS)].map((_, index) => {
                    const { date, day, jsDate, disabled } = getDayDetails({
                        index,
                        daysInMonth,
                        firstDay,
                        month,
                        year,
                    });

                    const isSelected = selected && isSameDay(jsDate, selected);

                    return (
                        <Button
                            key={`${index}-${date}-${day}`}
                            disabled={
                                disabled ||
                                moment(jsDate).isBefore(moment().startOf('day'))
                            }
                            onClick={() =>
                                dispatch({
                                    type: 'SELECT_DATE',
                                    payload: jsDate,
                                })
                            }
                            className={clsx('date-item', {
                                today: isToday(jsDate),
                                selected: isSelected,
                            })}
                            tabIndex={isSelected ? -1 : 0}
                        >
                            {date}
                        </Button>
                    );
                })}
            </div>
            <div className='time-picker'>
                <Button
                    variant='transparent'
                    onClick={() => dispatch({ type: 'DECREMENT_MONTH' })}
                >
                    <ChevronIcon />
                </Button>
                <span>
                    {new Intl.DateTimeFormat('en-US', {
                        month: 'long',
                    }).format(new Date(year, month))}
                </span>
                <Button
                    variant='transparent'
                    onClick={() => dispatch({ type: 'INCREMENT_MONTH' })}
                >
                    <ChevronIcon />
                </Button>
            </div>
            <div className='time-picker'>
                <Button
                    variant='transparent'
                    onClick={() => dispatch({ type: 'DECREMENT_TIME' })}
                >
                    <ChevronIcon />
                </Button>
                <span>
                    {new Intl.DateTimeFormat('en-US', {
                        timeStyle: 'short',
                    }).format(time)}
                </span>
                <Button
                    variant='transparent'
                    onClick={() => dispatch({ type: 'INCREMENT_TIME' })}
                >
                    <ChevronIcon />
                </Button>
            </div>
            <div className='controls'>
                <Button variant='danger' disabled={!selected} onClick={handleReset}>
                    Reset
                </Button>
                <Button
                    variant='primary'
                    disabled={
                        !selected ||
                        (moment(selected).isBefore(moment()) &&
                            moment(time).isBefore(moment()))
                    }
                    onClick={handleSave}
                >
                    Save
                </Button>
            </div>
        </>
    );
};

export default DatePicker;
